import React, { useEffect, useId, useState } from 'react';
import { BellRing, Check } from 'lucide-react';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import LoadingState from '../../components/LoadingState/LoadingState';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import { getUserReminders } from '../../api/reminders';
import { useI18n } from '../../i18n/I18nContext';
import { formatRelative } from '../../utils/datetime';
import { DELIVERY_STATUS_TONE } from '../Reminders/reminderView';
import { TASHKENT, ruleMeta } from '../Reminders/reminderUi';
import '../../components/Student360/Student360.css';

const FIRST = 5;

/**
 * "Eslatmalar tarixi": what the business sent this student (GET /crm/reminders/user/:id, newest
 * first): which channel, whether it arrived, when, and whether the link was opened. A card of the
 * page's right rail, drawn only for a role that works with reminders. A login the server refuses
 * (403) gets no card; any other failure says so with a retry.
 *
 *   <StudentReminders userId={id} reloadKey={n} />   // a new reloadKey reads the history again
 */
export default function StudentReminders({ userId, reloadKey = 0 }) {
  const { t } = useI18n();
  const uid = useId();
  const [state, setState] = useState({ phase: 'loading', items: [], error: null });
  const [retry, setRetry] = useState(0);
  const [all, setAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ phase: prev.items.length > 0 ? 'refreshing' : 'loading', items: prev.items, error: null }));
    getUserReminders(userId)
      .then((items) => { if (!cancelled) setState({ phase: 'ready', items, error: null }); })
      .catch((error) => { if (!cancelled) setState({ phase: 'error', items: [], error }); });
    return () => { cancelled = true; };
  }, [userId, reloadKey, retry]);

  // A message sent a moment ago is delivered by the queue a second or two later: look again twice.
  useEffect(() => {
    if (!reloadKey) return undefined;
    const timers = [3000, 7000].map((ms) => setTimeout(() => setRetry((n) => n + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, [reloadKey]);

  const { phase, items, error } = state;
  if (phase === 'error' && error?.code === 403) return null;

  const shown = all ? items : items.slice(0, FIRST);

  return (
    <section className="section-card s360-rail-card" aria-labelledby={`${uid}-title`}>
      <div className="section-head">
        <h2 id={`${uid}-title`} className="section-head__title">{t('reminders.student.historyTitle')}</h2>
        {items.length > 0 && <span className="ui-count">{items.length}</span>}
      </div>

      {phase === 'loading' && <LoadingState skeleton="list" rows={2} compact message={t('common.loading')} />}

      {phase === 'error' && (
        <InlineEmpty
          icon={<BellRing />}
          title={t('reminders.student.loadError')}
          action={(
            <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => setRetry((n) => n + 1)}>
              {t('reminders.student.retry')}
            </button>
          )}
        />
      )}

      {phase !== 'loading' && phase !== 'error' && items.length === 0 && (
        <InlineEmpty icon={<BellRing />} title={t('reminders.student.empty')} />
      )}

      {items.length > 0 && (
        <>
          <ul className="s360-list">
            {shown.map((item) => {
              const rule = item.ruleKey ? ruleMeta(item.ruleKey) : null;
              const title = item.campaign?.title
                || (item.ruleKey ? t('reminders.student.automatic') : t('reminders.student.untitled'));
              const hint = item.campaign?.title || !rule || rule.audience === null
                ? undefined
                : t(`reminders.rules.items.${item.ruleKey}.title`);
              return (
                <li key={item.id} className="s360-item">
                  <div className="s360-item__main">
                    <span className="s360-item__title" title={hint || title}>{title}</span>
                    <span className="s360-item__sub">
                      {t(`reminders.channel.${item.channel}`)}
                      {' · '}
                      {formatRelative(item.sentAt, { timeZone: TASHKENT })}
                    </span>
                  </div>
                  <div className="s360-item__side">
                    {item.clickedAt && (
                      <span className="sd-rem-opened" title={formatRelative(item.clickedAt, { timeZone: TASHKENT })}>
                        <Check aria-hidden="true" />
                        {t('reminders.student.opened')}
                      </span>
                    )}
                    <StatusBadge tone={DELIVERY_STATUS_TONE[item.status] || 'neutral'}>
                      {t(`reminders.delivery.${item.status}`)}
                    </StatusBadge>
                  </div>
                </li>
              );
            })}
          </ul>
          {items.length > FIRST && (
            <button type="button" className="s360-toggle sd-rem-more" onClick={() => setAll((v) => !v)}>
              {all ? t('reminders.student.showLess') : `${t('reminders.student.showAll')} (${items.length})`}
            </button>
          )}
        </>
      )}
    </section>
  );
}
